import React, { useMemo } from 'react';
import { mkNode, mkEdge, FlowCanvas } from './flowParts';

// Workflow diagrams for the Frame grab → develop → region grade pipeline, drawn
// with the same React Flow blocks as the other docs so they stay consistent.
// These mirror the real code: backend/api/stills.js (worker + computeAutoFrameGrade),
// backend/api/perceive.js (Lab-cluster perception), backend/api/grade.js
// (LUT + colour-key mask geq) and frontend/src/components/GradedCanvas.js (the
// WebGL2 preview that the bake matches). Keep in sync when the pipeline changes.

const C = {
  src: '#3fbf6f', // sources / master (green)
  edit: '#9b6cf2', // grade / edit (purple)
  state: '#e0a414', // intermediates / regions (amber)
  render: '#e0517a', // workers / bake (pink)
  out: '#5b8def', // outputs / derivatives (blue)
  join: '#14e0c8', // perception / measure / parity (teal)
};

// ---- 1. Frame grab → 16-bit master → derivatives -------------------------
const GRAB_NODES = [
  mkNode('grab', 0, 160, 'Frame Grabber', 'scrub clip · pick frame → queue', C.edit, 200),
  mkNode('worker', 300, 160, 'Stills worker', 'watches queue · concurrency pool', C.render, 200),
  mkNode('decode', 610, 160, 'Decode @ full res', 'RED .R3D SDK · or MP4 input-seek', C.src, 215),
  mkNode('master', 940, 160, '16-bit PNG master', 'flat log negative · full frame', C.src, 215),
  mkNode('auto', 1270, 40, 'Per-frame auto-grade', 'perception → grade (see engine)', C.join, 225),
  mkNode('bake', 1270, 290, 'Bake look + crop', 'ffmpeg → 16-bit intermediate', C.render, 215),
  mkNode('derivs', 1600, 290, 'Derivatives', 'thumb · magazine 3000 · full-res JPEG', C.out, 235),
  mkNode('store', 1930, 290, 'S3 + CDN · asset doc', 'grade stored · non-destructive', C.out, 220),
  mkNode('ui', 1930, 60, 'Photos · Lightbox', 'progressive → full-res on settle', C.edit, 220),
];
const GRAB_EDGES = [
  mkEdge('grab', 'worker', 'queued', { color: C.edit }),
  mkEdge('worker', 'decode', '', { color: C.render }),
  mkEdge('decode', 'master', '', { color: C.src }),
  mkEdge('master', 'auto', 'measure', { color: C.join }),
  mkEdge('master', 'bake', 'negative', { color: C.src }),
  mkEdge('auto', 'bake', 'per-frame grade', { animated: true, color: C.edit }),
  mkEdge('bake', 'derivs', '', { color: C.render }),
  mkEdge('derivs', 'store', '', { color: C.out }),
  mkEdge('store', 'ui', 'onSnapshot', { animated: true, color: C.out }),
];
const GRAB_LEGEND = [
  ['Grab / edit', C.edit],
  ['Worker / bake', C.render],
  ['Master', C.src],
  ['Perception', C.join],
  ['Outputs', C.out],
];
export function GrabFlow() {
  const nodes = useMemo(() => GRAB_NODES, []);
  const edges = useMemo(() => GRAB_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={GRAB_LEGEND} height={430} />;
}

// ---- 2. The per-frame auto-grade engine (perception-driven) --------------
const ENGINE_NODES = [
  mkNode('neg', 0, 175, '16-bit log negative', 'this frame, full res', C.src, 200),
  mkNode('ae', 300, 40, 'Auto-exposure', 'gray-median → stops (log-aware)', C.state, 205),
  mkNode('dev', 300, 300, 'Neutral develop', 'decode + DRT · no creative look', C.render, 205),
  mkNode('perc', 615, 300, 'Perception', 'CIE-Lab k-means clusters', C.join, 195),
  mkNode('reg', 905, 300, 'Regions', 'sky · water · whitewater · barrel · sand', C.state, 250),
  mkNode('wb', 1240, 30, 'Region white balance', 'foam / sky neutral ref — not gray-world', C.edit, 260),
  mkNode('mask', 1240, 175, 'Water colour-key mask', 'hue + chroma gate → deepen measured teal', C.edit, 270),
  mkNode('tonal', 1240, 320, 'Adaptive tonal', 'contrast · whites · blacks · spray protect', C.edit, 255),
  mkNode('grade', 1580, 175, 'Per-frame grade', 'stored on asset · fully editable', C.out, 210),
];
const ENGINE_EDGES = [
  mkEdge('neg', 'ae', 'median', { color: C.state }),
  mkEdge('neg', 'dev', 'decode', { color: C.render }),
  mkEdge('dev', 'perc', '', { color: C.join }),
  mkEdge('perc', 'reg', '', { color: C.join }),
  mkEdge('reg', 'wb', 'neutrals', { color: C.edit }),
  mkEdge('reg', 'mask', 'water hue/chroma', { color: C.edit }),
  mkEdge('reg', 'tonal', 'histogram', { color: C.edit }),
  mkEdge('ae', 'grade', 'exposure', { color: C.state }),
  mkEdge('wb', 'grade', '', { color: C.edit }),
  mkEdge('mask', 'grade', '', { animated: true, color: C.edit }),
  mkEdge('tonal', 'grade', '', { color: C.edit }),
];
const ENGINE_LEGEND = [
  ['Negative', C.src],
  ['Auto-exposure', C.state],
  ['Develop / measure', C.join],
  ['Region adjustments', C.edit],
  ['Grade', C.out],
];
export function AutoGradeEngine() {
  const nodes = useMemo(() => ENGINE_NODES, []);
  const edges = useMemo(() => ENGINE_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={ENGINE_LEGEND} height={450} />;
}

// ---- 3. Bake parity (preview == export) + refine loop --------------------
const BAKE_NODES = [
  mkNode('grade', 0, 185, 'Grade + colour-key mask', 'one non-destructive param set', C.edit, 220),
  mkNode('lut', 330, 60, '3D LUT (.cube)', 'applyGradePixel point pipeline', C.render, 215),
  mkNode('geq', 330, 320, 'Colour-key mask · geq', 'full-frame hue/chroma gate', C.render, 220),
  mkNode('glsl', 330, 185, 'WebGL2 preview', 'identical math + identical gate', C.join, 210),
  mkNode('bake', 660, 90, 'ffmpeg 16-bit bake', 'lut3d → spatial → mask geq', C.render, 215),
  mkNode('still', 980, 90, 'Baked derivatives', 'thumb · magazine · full-res', C.out, 205),
  mkNode('refine', 660, 320, 'Refine', 'in-app studio · or terminal (agentic)', C.edit, 220),
];
const BAKE_EDGES = [
  mkEdge('grade', 'lut', '', { color: C.render }),
  mkEdge('grade', 'geq', '', { color: C.render }),
  mkEdge('grade', 'glsl', 'live', { color: C.join }),
  mkEdge('lut', 'bake', '', { color: C.render }),
  mkEdge('geq', 'bake', '', { color: C.render }),
  mkEdge('bake', 'still', '', { color: C.out }),
  mkEdge('glsl', 'bake', 'Δ ≈ 1/255 parity', { animated: true, color: C.join }),
  mkEdge('still', 'refine', 'review', { color: C.out }),
  mkEdge('refine', 'grade', 'less teal · re-crop → re-bake', { animated: true, color: C.edit }),
];
const BAKE_LEGEND = [
  ['Grade', C.edit],
  ['LUT / mask / bake', C.render],
  ['Preview (parity)', C.join],
  ['Output', C.out],
];
export function GradeBakeParity() {
  const nodes = useMemo(() => BAKE_NODES, []);
  const edges = useMemo(() => BAKE_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={BAKE_LEGEND} height={440} />;
}
